import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePurchaseForm } from './viewmodel/usePurchaseForm';
import { fetchPurchaseById } from './controller/purchase-controller';
import SupplierDetailsForm from './components/SupplierDetailsForm';
import DepotSelector from '../components/DepotSelector';
import PurchaseItemsTable from './components/PurchaseItemsTable';
import PurchaseSummary from './components/purchaseSummary';

const EditPurchase = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    const isMountedRef = useRef(true);
    const fetchAttemptedRef = useRef(false);

    const emptyPurchaseItems = [{
        id: Math.random().toString(36).substr(2, 9),
        dbId: null,
        productId: null,
        variantId: null,
        name: '',
        reference: '',
        price: 0,
        quantity: 1,
        tva: 19,
        remise: 0,
        stock: 0,
        isVariant: false
    }];

    const purchaseFormHook = usePurchaseForm(navigate, true);

    const {
        formData,
        setFormData,
        purchaseItems,
        setPurchaseItems,
        errors,
        totals,
        updateTotals,
        isSubmitting,
        handleInputChange,
        handleAddItem,
        handleUpdateItem,
        handleRemoveItem,
        handleSubmit
    } = purchaseFormHook;

    const [formState, setFormState] = useState({
        reference: '',
        dateBonAchat: new Date().toISOString().slice(0, 16),
        supplier: {
            name: '',
            address: '',
            phone: '',
            email: '',
            city: '',
        },
        notes: ''
    });

    const [itemsState, setItemsState] = useState([...emptyPurchaseItems]);
    const [totalsState, setTotalsState] = useState({
        subtotalHT: 0,
        tva: 0,
        remise: 0,
        total: 0
    });

    useEffect(() => {
        console.log("setFormData type:", typeof setFormData);
        console.log("purchaseFormHook:", purchaseFormHook);
    }, [setFormData, purchaseFormHook]);

    useEffect(() => {
        console.log("Purchase ID from params:", id);
    }, [id]);

    useEffect(() => {
        if (fetchAttemptedRef.current) return;
        if (!id || typeof id !== 'string') {
            console.error("Invalid ID parameter:", id);
            setError("ID parameter is invalid");
            setLoading(false);
            return;
        }

        fetchAttemptedRef.current = true;

        const fetchPurchaseData = async () => {
            try {
                setLoading(true);
                console.log("Fetching purchase data for ID:", id);
                const purchaseId = parseInt(id, 10);
                if (isNaN(purchaseId)) {
                    throw new Error("Invalid purchase ID format");
                }

                const purchaseData = await fetchPurchaseById(purchaseId);
                console.log("Purchase data received:", purchaseData);

                if (!isMountedRef.current) return;

                if (purchaseData.entet) {
                    const newFormData = {
                        reference: purchaseData.entet.reference || '',
                        dateBonAchat: purchaseData.entet.dateBonCommande || new Date().toISOString().slice(0, 16),
                        supplier: {
                            id: purchaseData.entet.fournisseurId || null, // Add this line to include the supplier ID
                            name: purchaseData.entet.fournisseur?.name || '',
                            address: purchaseData.entet.fournisseur?.adresse || '',
                            phone: purchaseData.entet.fournisseur?.tel || '',
                            email: purchaseData.entet.fournisseur?.email || '',
                            city: '',
                        },
                        notes: purchaseData.entet.notes || '',
                    };

                    try {
                        if (typeof setFormData === 'function') {
                            setFormData(newFormData);
                        } else {
                            console.error("setFormData is not a function!", setFormData);
                            setFormState(newFormData);
                        }
                    } catch (err) {
                        console.error("Error setting form data:", err);
                        setFormState(newFormData);
                    }

                    const lines = purchaseData.line || [];

                    const mappedItems = lines.map(item => {
                        const productName = item.product?.name || 'Unknown Product';
                        const variantName = item.productVariant?.name || '';

                        const fullName = item.productVariantId ?
                            `${productName} - ${variantName}` :
                            productName;

                        const reference = item.productVariant?.reference || item.product?.reference || `Ref-${item.productId}`;

                        let tvaRate = item.tauxTva ?? 19;

                        if (!tvaRate && item.totalHt && item.totalTva) {
                            tvaRate = Math.round((item.totalTva / item.totalHt) * 100);
                        }

                        const quantity = item.quantite || 1;
                        const price = item.prixUnitaire || item.product?.prixAchatTTC || 0;

                        let remiseRate = item.tauxRemise ?? 0;
                        if (!remiseRate && item.totalRemise && price && quantity) {
                            const basePrice = price * quantity;
                            remiseRate = Math.round((item.totalRemise / basePrice) * 100);
                        }

                        return {
                            id: item.id?.toString() || Math.random().toString(36).substr(2, 9),
                            dbId: item.id,
                            productId: item.productId || null,
                            variantId: item.productVariantId || null,
                            name: fullName,
                            reference: reference,
                            price: price,
                            quantity: quantity,
                            tva: tvaRate,
                            remise: remiseRate,
                            stock: item.product?.stock ?? 0,
                            isVariant: !!item.productVariantId
                        };
                    });

                    if (mappedItems.length === 0) {
                        mappedItems.push(...emptyPurchaseItems);
                    }

                    try {
                        if (typeof setPurchaseItems === 'function') {
                            setPurchaseItems(mappedItems);
                        } else {
                            console.error("setPurchaseItems not available, using local state");
                            setItemsState(mappedItems);
                        }
                    } catch (err) {
                        console.error("Error setting purchase items:", err);
                        setItemsState(mappedItems);
                    }

                    const totalsFromPurchase = {
                        subtotalHT: purchaseData.entet.totalHt || 0,
                        tva: purchaseData.entet.totalTva || 0,
                        remise: purchaseData.entet.totalRemise || 0,
                        total: purchaseData.entet.totalTTC || 0
                    };

                    try {
                        // Don't call updateTotals with totals object during initial load
                        setTotalsState(totalsFromPurchase);

                        // If you want to use the hook's totals state, set it directly
                        if (purchaseFormHook.setTotals && typeof purchaseFormHook.setTotals === 'function') {
                            purchaseFormHook.setTotals(totalsFromPurchase);
                        }

                        console.log("Totals set successfully:", totalsFromPurchase);
                    } catch (err) {
                        console.error("Error setting totals:", err);
                        setTotalsState(totalsFromPurchase);
                    }

                    setDataLoaded(true);
                } else {
                    throw new Error("Invalid purchase data format");
                }

                setLoading(false);
            } catch (error) {
                if (!isMountedRef.current) return;
                console.error("Error fetching purchase:", error);
                setError(error.message || "Failed to load purchase data");
                setLoading(false);
            }
        };

        fetchPurchaseData();

        return () => {
            isMountedRef.current = false;
        };
    }, [id]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log("Form errors:", errors);
        }
    }, [errors]);

    // Add this useEffect after the data loading to recalculate totals from items
    useEffect(() => {
        if (purchaseItems && purchaseItems.length > 0 && dataLoaded) {
            console.log("Data loaded, recalculating totals from items:", purchaseItems);

            // Call updateTotals with the items array to recalculate
            if (typeof updateTotals === 'function') {
                console.log("Calling updateTotals with items array");
                updateTotals(purchaseItems);
            }
        }
    }, [purchaseItems, dataLoaded]); // ✅ Only watch purchaseItems and dataLoaded

    const handleLocalInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('supplier.')) {
            const field = name.split('.')[1];
            setFormState(prev => ({
                ...prev,
                supplier: {
                    ...prev.supplier,
                    [field]: value
                }
            }));
        } else {
            setFormState(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLocalAddItem = (newItem) => {
        console.log("EditPurchase - Adding item:", newItem);

        // Use functional update to ensure we're working with the latest state
        setPurchaseItems(currentItems => {
            console.log("EditPurchase - Current items in setState:", currentItems);

            // Check if the item already exists in the current items array
            const itemExists = currentItems.some(item =>
                (item.id === newItem.id) ||
                (item.variantId && newItem.variantId && item.variantId === newItem.variantId)
            );

            let updatedItems;

            if (itemExists) {
                console.log("EditPurchase - Item already exists:", newItem.id);
                // Update the quantity instead of replacing
                updatedItems = currentItems.map(item =>
                    ((item.id === newItem.id) ||
                        (item.variantId && newItem.variantId && item.variantId === newItem.variantId))
                        ? { ...item, quantity: (item.quantity || 0) + (newItem.quantity || 1) }
                        : item
                );
            } else {
                console.log("EditPurchase - Adding new item");
                // Add the new item to the existing array
                updatedItems = [...currentItems, newItem];
            }

            console.log("EditPurchase - Updated items array:", updatedItems);
            console.log("EditPurchase - Number of items in array:", updatedItems.length);

            // Force the updateTotals function with the new items
            setTimeout(() => {
                console.log("EditPurchase - Calling updateTotals with items:", updatedItems);
                updateTotals(updatedItems);
            }, 50);

            return updatedItems;
        });
    };

    const handleLocalUpdateItem = (id, updatedItem) => {
        setItemsState(prev =>
            prev.map(item => item.id === id ? { ...item, ...updatedItem } : item)
        );
    };

    const handleLocalRemoveItem = (id) => {
        setItemsState(prev => prev.filter(item => item.id !== id));
    };

    const handleLocalUpdateTotals = (newTotals) => {
        setTotalsState(newTotals);
    };

    const onSubmitForm = (e) => {
        e.preventDefault();
        console.log("Submit button clicked directly");
        handleSubmit(e, parseInt(id, 10));
    };

    if (loading) {
        return (
            <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
            </div>
        );
    }

    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto mb-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Link to="/admin/purchases" className="text-[#174540] hover:text-[#0d2926]">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">Modifier l'Achat #{id}</h1>
                    </div>

                    <Link
                        to="/admin/purchases"
                        className="text-sm font-medium text-[#174540] hover:text-[#0d2926]"
                    >
                        Retour à la liste
                    </Link>
                </div>

                <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                Vous modifiez l'achat #{id}. Les champs marqués d'un astérisque (*) sont obligatoires.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto">
                <form onSubmit={onSubmitForm} className="space-y-8">
                    <div className="bg-white overflow-hidden sm:rounded-lg border border-gray-200">
                        <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center">
                                <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
                                    <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">Détails de l'Achat</h3>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Modifiez les informations de l'achat ci-dessous
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-6 sm:px-8 sm:py-7">
                            <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Référence <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            name="reference"
                                            id="reference"
                                            value={formData?.reference || formState.reference}
                                            onChange={handleInputChange || handleLocalInputChange}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-100${errors.reference ? 'border-red-300 text-red-900 placeholder-red-300' : ''
                                                }`}
                                        />
                                        {errors.reference && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {errors.reference && <p className="mt-2 text-sm text-red-600">{errors.reference}</p>}
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="dateBonAchat" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Date d'achat <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="datetime-local"
                                            name="dateBonAchat"
                                            id="dateBonAchat"
                                            value={formData?.dateBonAchat || formState.dateBonAchat}
                                            onChange={handleInputChange || handleLocalInputChange}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-50 ${errors.dateBonAchat ? 'border-red-300 text-red-900 placeholder-red-300' : ''
                                                }`}
                                        />
                                        {errors.dateBonAchat && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {errors.dateBonAchat && <p className="mt-2 text-sm text-red-600">{errors.dateBonAchat}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <SupplierDetailsForm
                        formData={formData || formState}
                        onChange={handleInputChange || handleLocalInputChange}
                        errors={errors}
                    />

                    <div className="bg-white shadow rounded-md px-4 py-5 sm:px-6">
                        <DepotSelector
                            value={(formData || formState)?.depotId}
                            onChange={handleInputChange || handleLocalInputChange}
                            error={errors.depotId}
                        />
                    </div>

                    <PurchaseItemsTable
                        items={purchaseItems}
                        onAddItem={handleLocalAddItem}  // Use the enhanced local handler
                        onUpdateItem={handleUpdateItem}
                        onRemoveItem={handleRemoveItem}
                        totals={totals}
                        onUpdateTotals={updateTotals}
                    />

                    <PurchaseSummary
                        totals={
                            // Use local totals if hook totals are all zero or undefined
                            (totals && (totals.subtotalHT > 0 || totals.total > 0))
                                ? totals           // Use hook totals if they have values
                                : totalsState      // Use local totals as fallback
                        }
                        items={purchaseItems?.length > 0 ? purchaseItems : itemsState}
                    />

                    <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200">
                        <Link
                            to="/admin/purchases"
                            className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                        >
                            Annuler
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            onClick={(e) => {
                                console.log("Submit button clicked directly");
                                onSubmitForm(e);
                            }}
                            className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                        >
                            {isSubmitting ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Mise à jour...
                                </div>
                            ) : (
                                'Mettre à jour l\'achat'
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {Object.keys(errors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                    <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
                    <ul className="mt-2 text-sm text-red-700">
                        {Object.entries(errors).map(([key, value]) => (
                            <li key={key}>{key}: {value}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default EditPurchase;